// Category definitions + image selection for the update feed.
//
// Each card is classified into a category by keyword (tags + title), which drives
// the badge label, the accent colour, and the background image. Images come from a
// CURATED, PRE-VETTED pool of Unsplash nature/conservation photos per category
// (forests, wetlands, waterbodies, coast, wildlife, etc.) — so every image is
// guaranteed on-theme (no irrelevant images slipping through), while hashing by the
// article id keeps each card's photo stable and varied across the feed.
//
// All URLs were verified (HTTP 200 + image/*) when curated. To add/replace photos
// for a category, edit its array in IMAGES below (use Unsplash CDN URLs of the form
// https://images.unsplash.com/photo-<id>?auto=format&fit=crop&w=640&h=360&q=70).

export type Category = {
  key: string;
  label: string;
  accent: string;
  test: RegExp;
};

export const CATEGORIES: Category[] = [
  { key: 'wetland',     label: 'Wetland',                           accent: '#2dd4bf', test: /wetland|marsh|bog|fen|swamp|estuary/ },
  { key: 'water',       label: 'Water',                             accent: '#38bdf8', test: /lake|river|stream|creek|water|aquatic|flood|riparian|stormwater|watershed|drinking water/ },
  { key: 'coastal',     label: 'Coastal',                           accent: '#60a5fa', test: /coastal|ocean|marine|shoreline|foreshore|harbour|harbor|beach/ },
  { key: 'forest',      label: 'Forest & Urban Trees',              accent: '#10b981', test: /forest|tree|woodland|canopy|timber|urban heat/ },
  { key: 'grassland',   label: 'Grassland',                         accent: '#fbbf24', test: /grassland|prairie|meadow|pasture/ },
  { key: 'mountain',    label: 'Mountain',                          accent: '#a78bfa', test: /mountain|alpine|glacier|peak/ },
  { key: 'parks',       label: 'Parks & Trails',                    accent: '#84cc16', test: /park|trail|greenspace|green space|recreation|garden/ },
  { key: 'green-infra', label: 'Stormwater & Green Infrastructure', accent: '#22d3ee', test: /green infrastructure|rain garden|bioswale|low impact|permeable|drainage|sewer/ },
  { key: 'wildlife',    label: 'Wildlife & Biodiversity',           accent: '#f472b6', test: /wildlife|biodiversity|species|habitat|bird|fish|salmon|pollinator|ecosystem/ },
  { key: 'agriculture', label: 'Agriculture',                       accent: '#eab308', test: /agricultur|farm|crop|soil/ },
  { key: 'policy',      label: 'Policy & Planning',                 accent: '#cbd5e1', test: /policy|municipal|bylaw|governance|regulation|\bplan\b|budget|council|strategy/ },
];

export const DEFAULT_CATEGORY: Category = { key: 'nature', label: 'Nature', accent: '#10b981', test: /.*/ };

// Curated, verified Unsplash nature/conservation pools, keyed by category.
const IMAGES: Record<string, string[]> = {
  wetland: [
    'https://images.unsplash.com/photo-1627414879472-802bb4deab90?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1528732262645-b06fa3a79c9e?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1595529911736-d35f78538c3e?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1574786198875-49f5d09fe2d2?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1565846894054-51426d448b96?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1528164646014-c17e20821ec2?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1705989573404-9a580c149e09?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1631378726254-f30188042bdc?auto=format&fit=crop&w=640&h=360&q=70',
  ],
  water: [
    'https://images.unsplash.com/photo-1557456170-0cf4f4d0d362?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1587381419916-78fc843a36f8?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1567647753830-de3fe7ce9f28?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1495612778264-627cd2e6dfb3?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1546587348-d12660c30c50?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1561134643-668f9057cce4?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1516293635722-a0ae2709d570?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1448518340475-e3c680e9b4be?auto=format&fit=crop&w=640&h=360&q=70',
  ],
  coastal: [
    'https://images.unsplash.com/photo-1644123070703-2c9b9abafe43?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1510643585262-87b29e90207b?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1709423787965-55890e1b1715?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1645248952984-0ad461efbfe7?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1645249044358-c046fbcccb77?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1512370122105-62410b16f89a?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1706555693525-fb90cbe18826?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1593999439603-1ec389c698e6?auto=format&fit=crop&w=640&h=360&q=70',
  ],
  forest: [
    'https://images.unsplash.com/photo-1511497584788-876760111969?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1473448912268-2022ce9509d8?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1599940824399-b87987ceb72a?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1446034295857-c39f8844fad4?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1614200991676-910c80a78ed4?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1592303166784-4b7ceb49c5db?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1597133588002-5a620508f971?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1611174797197-8533987c6bf4?auto=format&fit=crop&w=640&h=360&q=70',
  ],
  grassland: [
    'https://images.unsplash.com/photo-1601839817240-d0d7245a4048?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1626364424120-81fcc7f06ebd?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1610848554990-1715bdb9beeb?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1631984140724-7882f63ebc4c?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1633566826797-9766f7af71d2?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1620187966558-26c5fb00438d?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1470240731273-7821a6eeb6bd?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1617067128946-8c4f807d91e1?auto=format&fit=crop&w=640&h=360&q=70',
  ],
  mountain: [
    'https://images.unsplash.com/photo-1539667547529-84c607280d20?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1564032236772-dfc27a12feda?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1489363855452-7327672b1608?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1461723279703-234cf6425457?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1599313731897-257f8334eaf1?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1583417123076-ab17044e5982?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1540514869736-ba72790e9a08?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1581260466152-d2c0303e54f5?auto=format&fit=crop&w=640&h=360&q=70',
  ],
  parks: [
    'https://images.unsplash.com/photo-1610878180933-123728745d22?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1603486002664-a7319421e133?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1630618359150-409abae1bced?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1568036998293-09f73fcd6835?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1639436330343-e7b8a310f19a?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1501979735065-dfe1580b4a49?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1592859600972-1b0834d83747?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1605196560547-b2f7281b7355?auto=format&fit=crop&w=640&h=360&q=70',
  ],
  'green-infra': [
    'https://images.unsplash.com/photo-1509635022432-0220ac12960b?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1718546837542-ed28109f3450?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1522556256306-727dcd29c799?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1691067987381-29616a126e28?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1637202467427-cbc8f49a92ac?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1595797475107-06c1bea78679?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1599277100479-3252d492a19a?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1686847909131-65c811c721f7?auto=format&fit=crop&w=640&h=360&q=70',
  ],
  wildlife: [
    'https://images.unsplash.com/photo-1510992107199-e5224f959d6c?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1651673153662-86d3fde39c9f?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1696353342850-e46d15d5ac31?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1673560443506-aab509369339?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1707536484364-b20b679a9491?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1719318694296-f7290127f39c?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1640650762589-aabc8a1987c1?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1640650762530-39ef8b1066ac?auto=format&fit=crop&w=640&h=360&q=70',
  ],
  agriculture: [
    'https://images.unsplash.com/photo-1596987851982-3b90e09ab4ac?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1612298389479-585588cab492?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1596988013069-8742e922b785?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1668379399907-411e6aa74eb9?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1623428719334-5d304060e0e3?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1585384749492-53701eec1cdf?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1500382017468-9049fed747ef?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1559884743-74a57598c6c7?auto=format&fit=crop&w=640&h=360&q=70',
  ],
  policy: [
    'https://images.unsplash.com/photo-1539667547529-84c607280d20?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1564032236772-dfc27a12feda?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1489363855452-7327672b1608?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1497373637916-e47a55e22d0a?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1531512073830-ba890ca4eba2?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1506104489822-562ca25152fe?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1610878180933-123728745d22?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1603486002664-a7319421e133?auto=format&fit=crop&w=640&h=360&q=70',
  ],
  nature: [
    'https://images.unsplash.com/photo-1448375240586-882707db888b?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1542273917363-3b1817f69a2d?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1507041957456-9c397ce39c97?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1503435980610-a51f3ddfee50?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1594741873407-c292ca075f8b?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1609568175100-55a3902e3188?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1602519958552-07eb454c9a78?auto=format&fit=crop&w=640&h=360&q=70',
    'https://images.unsplash.com/photo-1565015931955-2febbbdef4b5?auto=format&fit=crop&w=640&h=360&q=70',
  ],
};

// Wide scenic landscapes for the 3 hero cards.
const HERO_IMAGES: string[] = [
  'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?auto=format&fit=crop&w=640&h=360&q=70',
  'https://images.unsplash.com/photo-1527489377706-5bf97e608852?auto=format&fit=crop&w=640&h=360&q=70',
  'https://images.unsplash.com/photo-1497373637916-e47a55e22d0a?auto=format&fit=crop&w=640&h=360&q=70',
  'https://images.unsplash.com/photo-1531512073830-ba890ca4eba2?auto=format&fit=crop&w=640&h=360&q=70',
  'https://images.unsplash.com/photo-1506104489822-562ca25152fe?auto=format&fit=crop&w=640&h=360&q=70',
  'https://images.unsplash.com/photo-1489447068241-b3490214e879?auto=format&fit=crop&w=640&h=360&q=70',
  'https://images.unsplash.com/photo-1513564774965-ac25ddf81e1e?auto=format&fit=crop&w=640&h=360&q=70',
  'https://images.unsplash.com/photo-1493246507139-91e8fad9978e?auto=format&fit=crop&w=640&h=360&q=70',
  'https://images.unsplash.com/photo-1610141353646-14306dc6a9ab?auto=format&fit=crop&w=640&h=360&q=70',
  'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?auto=format&fit=crop&w=640&h=360&q=70',
];

const NATURE = IMAGES.nature;

export function classify(text: string): Category {
  const t = (text || '').toLowerCase();
  return CATEGORIES.find(c => c.test.test(t)) ?? DEFAULT_CATEGORY;
}

// Stable string hash → non-negative int.
function hash(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

// Image + meta for a content card. `seed` (the article id) keeps the image stable
// for that article while differing across articles in the same category. Both the
// primary and the onError fallback come from curated nature pools, so a card can
// never show a non-nature image.
export function cardImage(text: string, seed: string): { url: string; fallback: string; label: string; accent: string } {
  const c = classify(text);
  const pool = (IMAGES[c.key] && IMAGES[c.key].length) ? IMAGES[c.key] : NATURE;
  const h = hash(seed + c.key);
  return {
    url: pool[h % pool.length],
    fallback: NATURE[h % NATURE.length],
    label: c.label,
    accent: c.accent,
  };
}

// One of the curated wide landscapes for each hero card. `rotation` shifts which
// show (e.g. by day or per crawl); each of the 3 cards stays distinct.
export function heroImage(cardIndex: number, rotation: number): string {
  return HERO_IMAGES[(cardIndex + rotation) % HERO_IMAGES.length];
}
